import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, AlertTriangle, Plane, UserMinus, Clock, LogIn, LogOut, Coffee, Play,
  Paperclip, FileText, Gift, CalendarMinus,
} from 'lucide-react';
import {
  createEvent, uploadDocument, sendMessage, buildGeoInfoWithApprover, getEventsByRange,
  searchWorkers, updateWorker, EVENT_TYPES, APPROVAL_STATUS,
} from '../services/api';
import { appendSentMessage } from '../lib/sentMessagesCache';
import { loadVacationCapCheck } from '../services/vacationCap';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, todayInZone, formatHmInZone, zonedDateTimeToInstant,
} from '../lib/workerTime';
import { COMPENSATION_FULL_DAY_END_HM } from '../lib/hourBalance';
import {
  HOUR_RANGE_KINDS, hourRangeError, startHourRejectionError, isStartHourRejection,
} from '../utils/assignHourRange';
import { loadDayConflicts, dayConflictWarning } from '../utils/dayConflict';
import {
  canForceStartHour, forceStartHourExplanation, runSteps, runWithStartHourDisabled,
} from '../utils/forceStartHour';

const FILE_ACCEPT = 'image/*,application/pdf';
const FILE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// "Hoy" / "ahora" en la zona del TRABAJADOR al que se asigna el evento.
const todayStr = (tz = DEFAULT_TIME_ZONE) => todayInZone(tz);
const nowHm = (tz = DEFAULT_TIME_ZONE) => formatHmInZone(Date.now(), tz);

// Fecha + hora elegidas → instante, interpretadas en la zona del trabajador
// dueño del evento (no la del admin ni la del navegador).
const buildIso = (date, hm, tz = DEFAULT_TIME_ZONE) => {
  if (!date || !hm) return null;
  const d = zonedDateTimeToInstant(date, hm, tz);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const MODES = [
  { key: 'vacaciones', label: 'Vacaciones', icon: Plane },
  { key: 'baja',       label: 'Día completo', icon: UserMinus },
  { key: 'manual',     label: 'Evento manual', icon: Clock },
];

// Tipos de fichaje manual. "Compensadas" y "Ausencia por horas" son los dos
// únicos que NO crean un evento suelto: crean un PAR (8/9 compensación,
// 10/11 ausencia) acotado a un tramo horario del mismo día. Ver RANGE_TYPES.
const MANUAL_TYPES = [
  { value: EVENT_TYPES.CLOCK_IN,    label: 'Entrada',     icon: LogIn },
  { value: EVENT_TYPES.CLOCK_OUT,   label: 'Salida',      icon: LogOut },
  { value: EVENT_TYPES.PAUSE_START, label: 'Inicio pausa', icon: Coffee },
  { value: EVENT_TYPES.PAUSE_END,   label: 'Fin pausa',   icon: Play },
  { value: EVENT_TYPES.COMPENSATION_START, label: 'Compensadas', icon: Gift },
  { value: EVENT_TYPES.ABSENCE_START, label: 'Ausencia por horas', icon: CalendarMinus },
];

// Los dos tipos "de tramo" se identifican por el INICIO del par.
const COMPENSATION_RANGE = EVENT_TYPES.COMPENSATION_START;
const ABSENCE_RANGE = EVENT_TYPES.ABSENCE_START;
const RANGE_TYPES = [COMPENSATION_RANGE, ABSENCE_RANGE];

// Hora de fin reservada para las compensaciones de DÍA COMPLETO
// (isFullDayCompensationRange). Un tramo NUNCA puede terminar ahí o el
// cómputo lo leería como día libre entero.
const MAX_COMPENSATION_END_HM = COMPENSATION_FULL_DAY_END_HM;

// Tramo propuesto al elegir un tipo de tramo: la hora anterior a "ahora" (el
// caso típico es "se ha ido una hora antes"). Se deja siempre dentro del día
// y con fin < 23:59, así el formulario nace válido y el admin solo ajusta.
const defaultCompensationRange = (tz) => {
  const [h, m] = String(nowHm(tz) || '00:00').split(':').map(Number);
  const endMin = Math.min(Math.max((h * 60 + m) || 60, 60), 23 * 60 + 58);
  const startMin = endMin - 60;
  const hm = (total) => `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  return { start: hm(startMin), end: hm(endMin) };
};

// `events` (opcional): los fichajes que la ficha del trabajador ya tiene
// cargados. Solo se usan como PLAN B para el aviso de solape — vienen
// recortados al rango del histórico (filterEventsToLocalRange), así que un
// periodo que empiece antes de ese rango llega sin su START. Lo normal es
// consultar la ventana exacta con getEventsByRange.
const AssignEventModal = ({ open, worker, onClose, onSubmitted, events = null }) => {
  const { user } = useAuth();
  const toast = useToast();
  // Zona del trabajador que RECIBE el evento (Canarias / península).
  const tz = useMemo(
    () => timeZoneForWorker(worker),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worker?.autonomousCommunity, worker?.community, worker?.province]
  );
  const tzHint = timeZoneLabel(tz);
  const [mode, setMode] = useState('vacaciones');

  // Rango (vacaciones / baja)
  const [startDate, setStartDate] = useState(() => todayStr(tz));
  const [endDate, setEndDate] = useState(() => todayStr(tz));

  // Justificante para baja médica (PDF / imagen). Obligatorio en modo 'baja'.
  const [bajaFile, setBajaFile] = useState(null);
  const [bajaFileError, setBajaFileError] = useState(null);

  // Manual (un único evento)
  const [manualType, setManualType] = useState(EVENT_TYPES.CLOCK_IN);
  const [manualDate, setManualDate] = useState(() => todayStr(tz));
  const [manualTime, setManualTime] = useState(() => nowHm(tz));

  // Tramo horario (mismo día, hora de fin posterior a la de inicio). Solo se
  // usa con los tipos de RANGE_TYPES: compensadas y ausencia por horas.
  const [compStart, setCompStart] = useState('');
  const [compEnd, setCompEnd] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Propuesta de "Crear igualmente" tras un rechazo del servidor por la hora
  // de entrada: { steps, created, successMsg }. null = sin propuesta abierta.
  const [forcePrompt, setForcePrompt] = useState(null);

  useEffect(() => {
    if (open) {
      setMode('vacaciones');
      setStartDate(todayStr(tz));
      setEndDate(todayStr(tz));
      setManualType(EVENT_TYPES.CLOCK_IN);
      setManualDate(todayStr(tz));
      setManualTime(nowHm(tz));
      setCompStart('');
      setCompEnd('');
      setBajaFile(null);
      setBajaFileError(null);
      setError(null);
      setForcePrompt(null);
    }
  }, [open, tz]);

  // La propuesta de forzar lleva dentro los pasos YA construidos (con su
  // fecha y su hora). Si el admin toca el formulario, esos pasos dejan de
  // corresponder con lo que ve: se descarta la propuesta y que vuelva a dar
  // a "Asignar".
  useEffect(() => {
    setForcePrompt(null);
  }, [mode, manualType, manualDate, manualTime, compStart, compEnd]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setBajaFileError(null);
    if (!f) { setBajaFile(null); return; }
    if (f.size > FILE_MAX_BYTES) {
      setBajaFileError('El archivo supera 10 MB.');
      setBajaFile(null);
      return;
    }
    setBajaFile(f);
  };

  const isRangeMode = mode === 'vacaciones' || mode === 'baja';
  const isCompensationRange = mode === 'manual' && manualType === COMPENSATION_RANGE;
  const isAbsenceRange = mode === 'manual' && manualType === ABSENCE_RANGE;
  // Los dos tipos que piden "Desde / Hasta" en vez de una sola "Hora".
  const isHourRange = isCompensationRange || isAbsenceRange;

  const validRange = useMemo(() => {
    if (!isRangeMode) return true;
    if (!startDate || !endDate) return false;
    // Claves 'YYYY-MM-DD': la comparación de strings es la de calendario.
    return startDate <= endDate;
  }, [isRangeMode, startDate, endDate]);

  // Validación del tramo (compensadas / ausencia por horas): ambas horas, fin
  // POSTERIOR al inicio (mismo día) y fin anterior a 23:59, que está reservado
  // para los días completos. La regla vive en utils/assignHourRange.js.
  const compRangeError = useMemo(() => {
    if (!isHourRange) return null;
    return hourRangeError({
      kind: isAbsenceRange ? HOUR_RANGE_KINDS.ABSENCE : HOUR_RANGE_KINDS.COMPENSATION,
      start: compStart,
      end: compEnd,
      maxEndHm: MAX_COMPENSATION_END_HM,
    });
  }, [isHourRange, isAbsenceRange, compStart, compEnd]);

  if (!open || !worker?.id) return null;

  // Solo quien gestiona a este trabajador puede forzar un fichaje saltándose
  // temporalmente su control de hora de entrada — y nunca sobre sí mismo.
  const canForce = canForceStartHour(user, worker);

  // Relee la ficha del trabajador. El GET /api/company/person/:q busca por
  // nombre/email/nif/teléfono (NO por id), así que se pide la lista y se
  // localiza por id — el mismo patrón que usa WorkerDetailPage.
  const reloadWorker = async () => {
    const all = await searchWorkers('');
    return (Array.isArray(all) ? all : []).find((w) => String(w.id) === String(worker.id)) || null;
  };

  // Segunda oportunidad: desactivar el control, crear lo que faltaba y dejarlo
  // como estaba. Toda la secuencia vive en utils/forceStartHour.js.
  const handleForce = async () => {
    const plan = forcePrompt;
    if (!plan) return;
    setForcePrompt(null);
    setError(null);
    setSubmitting(true);
    try {
      const res = await runWithStartHourDisabled({
        worker,
        readWorker: reloadWorker,
        setEnforceStartHour: (value) => updateWorker(worker.id, { enforceStartHour: value }),
        steps: plan.steps,
        alreadyCreated: plan.created,
      });
      onSubmitted?.();
      if (res.level === 'success') {
        toast.success(`${plan.successMsg} ${res.message}`);
        onClose?.();
        return;
      }
      // Restauración fallida o sin verificar: el modal se queda abierto con el
      // aviso en pantalla, que es lo que el admin tiene que leer entero.
      if (res.level === 'warning') toast.warning(res.message);
      else toast.error(res.message);
      setError(res.message);
    } catch (err) {
      setError(err?.message || 'No se pudo forzar la creación del fichaje.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError(null);
    setForcePrompt(null);

    try {
      if (isRangeMode) {
        if (!validRange) {
          setError('La fecha "Desde" debe ser anterior o igual a "Hasta".');
          return;
        }
        // El justificante es OPCIONAL: una falta justificada no obliga a
        // adjuntar documento (decisión de producto). Si el admin lo adjunta,
        // se sube igual (ver más abajo); si no, la ausencia se registra sin él.
        // Vacaciones / baja → par start (00:00) + end (23:59) ya aprobados.
        // Sigue el mismo patrón que AssignCompensationModal.
        const startIso = buildIso(startDate, '00:00', tz);
        const endIso = buildIso(endDate, '23:59', tz);
        if (!startIso || !endIso) {
          setError('Fechas no válidas.');
          return;
        }
        // Vacaciones: aviso de cupo anual ANTES de crear nada (mismo cómputo
        // que Aprobaciones y los contadores: src/services/vacationCap.js). No
        // bloquea: si se pasa, el admin confirma explícitamente; si no se
        // puede comprobar, se avisa y se sigue.
        if (mode === 'vacaciones') {
          setSubmitting(true);
          let cap;
          try {
            cap = await loadVacationCapCheck({ worker, request: { start: startIso, end: endIso }, verb: 'asignar' });
          } catch {
            cap = { status: 'error', message: 'No se pudo comprobar el cupo anual de vacaciones.' };
          }
          if (cap.status === 'error') {
            toast.warning(cap.message);
          } else if (cap.status === 'ok' && cap.message
            && !window.confirm(`${cap.message}\n\n¿Asignar igualmente?`)) {
            setSubmitting(false);
            return;
          }
        }
        const startType = mode === 'vacaciones'
          ? EVENT_TYPES.VACATION_START
          : EVENT_TYPES.SICK_LEAVE_START;
        const endType = mode === 'vacaciones'
          ? EVENT_TYPES.VACATION_END
          : EVENT_TYPES.SICK_LEAVE_END;
        setSubmitting(true);
        // Audit trail: el admin que asigna queda registrado en geoInfo.
        const apprGeoInfo = buildGeoInfoWithApprover(null, user);
        await createEvent({
          workerId: worker.id,
          eventType: startType,
          timestamp: startIso,
          approvalStatus: APPROVAL_STATUS.APPROVED,
          geoInfo: apprGeoInfo,
        });
        await createEvent({
          workerId: worker.id,
          eventType: endType,
          timestamp: endIso,
          approvalStatus: APPROVAL_STATUS.APPROVED,
          geoInfo: apprGeoInfo,
        });
        // Si es baja, subimos el justificante asociado al trabajador. El
        // upload tiene su propio companyId/workplaceId/uploaderId — el
        // uploaderId es el ADMIN (current user), no el trabajador.
        // Los eventos YA están creados: si el justificante falla no dejamos
        // el modal abierto (reintentar duplicaría la ausencia); se avisa.
        let uploadError = null;
        if (mode === 'baja' && bajaFile) {
          try {
            await uploadDocument({
              file: bajaFile,
              companyId: worker.companyId,
              workplaceId: worker.workingSite || 0,
              uploaderId: user?.id,
              description: `Justificante día completo ${startDate} a ${endDate}`,
            });
          } catch (upErr) {
            uploadError = upErr;
          }
        }
        toast.success(FEEDBACK.periodAssigned({ kind: mode, start: startDate, end: endDate, workerName: worker.name }));
        if (uploadError) toast.warning(FEEDBACK.attachmentFailed(uploadError));
        // Notificación al trabajador cuando el admin le asigna vacaciones.
        // Si el backend de mensajes está caído (403 conocido), el catch
        // silencia el error — la asignación ya está hecha y queda como
        // mensaje al trabajador en cuanto se restablezca el servicio.
        if (mode === 'vacaciones' && user?.id && worker?.id) {
          try {
            const who = user.name || user.email || `Admin #${user.id}`;
            const created = await sendMessage({
              senderId: user.id,
              receiverId: worker.id,
              content: `${who} te ha asignado un periodo de vacaciones del ${startDate} al ${endDate}.`,
            });
            // Trackeamos el mensaje en la caché de "enviados" para que se
            // vea en el chat del admin con el trabajador.
            appendSentMessage(user.id, created);
          } catch {
            toast.warning(FEEDBACK.workerNotNotified);
          }
        }
      } else {
        // ── Fichaje manual: evento suelto o par de tramo ───────────────────
        // Las tres variantes (tramo compensado, ausencia por horas y fichaje
        // suelto) se resuelven igual: se arma la lista de PASOS de creación y
        // se ejecuta en orden. Tenerlos como lista es lo que permite, si el
        // servidor rechaza por hora de entrada, reintentar EXACTAMENTE lo que
        // quedó sin crear (ver utils/forceStartHour.js).
        //
        // · Compensadas (8/9): decisión de producto "Día −1h, bolsa −1h". Esas
        //   horas NO son tiempo trabajado y NO reducen la jornada esperada: el
        //   día sale en déficit y la bolsa baja. Lo que aporta el tramo es la
        //   JUSTIFICACIÓN (el día deja de contar como incidencia).
        // · Ausencia por horas (10/11): regla de producto 2026-09-22, SE
        //   RECUPERA. No reduce jornada, no cuenta como trabajada y no genera
        //   offset de bolsa — el trabajador debe esas horas.
        // Un tramo NUNCA termina a las 23:59 (compRangeError lo impide): esa
        // marca la reserva el día completo (isFullDayCompensationRange).
        const apprGeoInfo = buildGeoInfoWithApprover(null, user);
        const mkStep = (label, eventType, timestamp) => ({
          label,
          run: () => createEvent({
            workerId: worker.id,
            eventType,
            timestamp,
            approvalStatus: APPROVAL_STATUS.APPROVED,
            geoInfo: apprGeoInfo,
          }),
        });

        let steps = [];
        let successMsg = '';
        if (isHourRange) {
          if (compRangeError) {
            setError(compRangeError);
            return;
          }
          const startIso = buildIso(manualDate, compStart, tz);
          const endIso = buildIso(manualDate, compEnd, tz);
          if (!startIso || !endIso) {
            setError('Fecha/horas no válidas.');
            return;
          }
          const startType = isCompensationRange ? EVENT_TYPES.COMPENSATION_START : EVENT_TYPES.ABSENCE_START;
          const endType = isCompensationRange ? EVENT_TYPES.COMPENSATION_END : EVENT_TYPES.ABSENCE_END;
          steps = [
            mkStep('el inicio del tramo', startType, startIso),
            mkStep('el fin del tramo', endType, endIso),
          ];
          successMsg = isCompensationRange
            ? FEEDBACK.compensationHoursAssigned({
              dayKey: manualDate, start: compStart, end: compEnd, workerName: worker.name,
            })
            : FEEDBACK.hourlyAbsenceAssigned({
              dayKey: manualDate, start: compStart, end: compEnd, workerName: worker.name,
            });
        } else {
          const ts = buildIso(manualDate, manualTime, tz);
          if (!ts) {
            setError('Fecha/hora no válidas.');
            return;
          }
          const typeLabel = (MANUAL_TYPES.find((t) => t.value === manualType)?.label || 'fichaje').toLowerCase();
          steps = [mkStep(`el fichaje de ${typeLabel}`, manualType, ts)];
          successMsg = FEEDBACK.manualEventAssigned({
            eventType: manualType, dayKey: manualDate, time: manualTime, workerName: worker.name,
          });
        }

        setSubmitting(true);

        // AVISO DE SOLAPE: el día puede estar ya marcado como vacaciones,
        // ausencia de día completo, ausencia por horas o compensación. Avisa y
        // deja decidir — no bloquea. Si la consulta falla no hay aviso: un
        // fallo de red no puede impedir asignar un fichaje.
        const conflicts = await loadDayConflicts({
          fetchEvents: (from, to) => getEventsByRange(worker.id, from, to),
          dayKey: manualDate,
          timeZone: tz,
          fallbackEvents: events,
        });
        const conflictWarning = dayConflictWarning(conflicts, {
          action: isHourRange ? 'añadir este tramo' : 'añadir un fichaje',
        });
        if (conflictWarning && !window.confirm(conflictWarning)) {
          setSubmitting(false);
          return;
        }

        // El backend aplica enforceStartHour a TODOS los tipos salvo el 2 y el
        // 4: cualquiera de estos fichajes puede salir rechazado si empieza
        // antes de la hora de entrada del trabajador. La hora pedida NO se
        // toca — al admin que gestiona a esa persona se le ofrece forzar.
        const { created, failed, rest } = await runSteps(steps);
        if (failed) {
          if (created.length) onSubmitted?.();
          const wrapped = startHourRejectionError(failed.error, {
            what: isHourRange
              ? (isCompensationRange ? 'el tramo de horas compensadas' : 'la ausencia por horas')
              : 'el fichaje',
            workerName: worker.name,
            dayKey: manualDate,
            start: isHourRange ? compStart : manualTime,
            end: isHourRange ? compEnd : null,
          });
          // Rechazo por hora de entrada + admin con permiso: se le ofrece
          // forzar en vez de dejarle en un callejón sin salida. Si dice que
          // no, se queda el mensaje explicando por qué no se ha creado.
          if (isStartHourRejection(failed.error) && canForce) {
            setForcePrompt({ steps: rest, created, successMsg, rejectionMessage: wrapped?.message });
            setSubmitting(false);
            return;
          }
          // Par a medias: el inicio ya existe y el fin no. Hay que decirlo —
          // reintentar a ciegas duplicaría el inicio.
          if (created.length && wrapped?.reason !== 'session-ended') {
            const partial = new Error(
              `Se creó ${created.join(' y ')}, pero NO ${failed.label}: ${wrapped?.message || 'error desconocido'} `
              + 'Revisa el día antes de repetirlo: volver a asignarlo duplicaría lo que ya está creado.'
            );
            partial.status = wrapped?.status ?? null;
            partial.reason = 'partial';
            throw partial;
          }
          throw wrapped;
        }
        toast.success(successMsg);
      }
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo asignar el evento.');
    } finally {
      setSubmitting(false);
    }
  };

  const ActiveIcon = MODES.find((m) => m.key === mode)?.icon || Clock;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <form onSubmit={handleSubmit} className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Asignar evento</div>
            <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
              <ActiveIcon size={18} className="text-primary-darker" /> {MODES.find((m) => m.key === mode)?.label}
            </h2>
            <div className="text-xs text-ink-muted mt-0.5 truncate">A {worker.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-md transition" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Tabs */}
          <div className="grid grid-cols-3 gap-2">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                    active ? 'bg-primary-soft border-primary text-primary-darker' : 'bg-white border-slate-200 text-ink-muted hover:text-ink'
                  }`}
                >
                  <Icon size={14} /> {m.label}
                </button>
              );
            })}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-ink-muted">
            {mode === 'vacaciones' && 'Vacaciones aprobadas directamente por el admin. Cubrirá los días completos del rango (00:00 → 23:59).'}
            {mode === 'baja' && 'Ausencia de día completo (justificada) aprobada directamente por el admin. Cubrirá los días enteros del rango (00:00 → 23:59).'}
            {mode === 'manual' && !isHourRange && 'Crea un único fichaje (entrada / salida / pausa) a la fecha y hora indicadas. Queda como aprobado, sin pasar por solicitudes.'}
            {isCompensationRange && 'Marca un tramo de horas como COMPENSADAS (p. ej. sale una hora antes). No cuentan como trabajadas ni reducen su jornada: el día queda justificado y esas horas se descuentan de su bolsa. Para regalarle el día entero, usa "Asignar compensación".'}
            {isAbsenceRange && 'Marca un tramo de horas como AUSENCIA POR HORAS (médico, gestión…), ya aprobada. Esas horas SE RECUPERAN: no cuentan como trabajadas y no reducen su jornada, así que quedan a deber. Para un día entero usa la pestaña "Día completo".'}
          </div>

          {isRangeMode ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Desde</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Hasta</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                    required
                  />
                </div>
              </div>

              {mode === 'baja' && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-1.5">
                    <Paperclip size={11} /> Justificante (opcional — médico, boda, funeral…)
                  </label>
                  <label className="flex items-center gap-3 px-3 py-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary-soft/30 transition-colors">
                    <input
                      type="file"
                      accept={FILE_ACCEPT}
                      onChange={onFileChange}
                      className="hidden"
                    />
                    <FileText size={16} className={bajaFile ? 'text-primary-darker' : 'text-ink-muted'} />
                    <div className="flex-1 min-w-0 text-sm">
                      {bajaFile ? (
                        <>
                          <div className="font-semibold text-ink truncate">{bajaFile.name}</div>
                          <div className="text-xs text-ink-muted">{(bajaFile.size / 1024).toFixed(0)} KB · {bajaFile.type || 'archivo'}</div>
                        </>
                      ) : (
                        <span className="text-ink-muted">Seleccionar PDF o imagen…</span>
                      )}
                    </div>
                  </label>
                  {bajaFileError && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-danger">
                      <AlertTriangle size={11} /> {bajaFileError}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Tipo de fichaje</label>
                <div className="grid grid-cols-2 gap-2">
                  {MANUAL_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = manualType === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => {
                          setManualType(t.value);
                          // Al entrar en un tipo de tramo (compensadas /
                          // ausencia por horas) proponemos un tramo válido
                          // para no arrancar con el botón bloqueado.
                          if (RANGE_TYPES.includes(t.value) && !compStart && !compEnd) {
                            const r = defaultCompensationRange(tz);
                            setCompStart(r.start);
                            setCompEnd(r.end);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                          active ? 'bg-primary-soft border-primary text-primary-darker' : 'bg-white border-slate-200 text-ink-muted hover:text-ink'
                        }`}
                      >
                        <Icon size={13} /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Fecha</label>
                  <input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                    required
                  />
                </div>
                {isHourRange ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Desde</label>
                      <input
                        type="time"
                        value={compStart}
                        onChange={(e) => setCompStart(e.target.value)}
                        max={MAX_COMPENSATION_END_HM}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Hasta</label>
                      <input
                        type="time"
                        value={compEnd}
                        onChange={(e) => setCompEnd(e.target.value)}
                        max={MAX_COMPENSATION_END_HM}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">Hora</label>
                    <input
                      type="time"
                      value={manualTime}
                      onChange={(e) => setManualTime(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                )}
              </div>
              {tzHint && <div className="text-[11px] text-ink-muted -mt-2">{tzHint}</div>}
              {isHourRange && compRangeError && (compStart || compEnd) && (
                <div className="inline-flex items-start gap-1.5 text-xs text-danger">
                  <AlertTriangle size={11} className="shrink-0 mt-0.5" /> {compRangeError}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-danger/5 border border-danger/30 rounded-lg p-3 text-sm">
              <AlertTriangle size={16} className="text-danger flex-shrink-0 mt-0.5" />
              <span className="text-danger whitespace-pre-line">{error}</span>
            </div>
          )}

          {/* Rechazo por hora de entrada con salida: forzar la creación
              desactivando un instante el control del trabajador. Acción
              SIEMPRE explícita — nunca se hace sola. */}
          {forcePrompt && (
            <div className="bg-warning/10 border border-warning/40 rounded-lg p-3 text-sm space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
                <span className="text-ink whitespace-pre-line">
                  {forceStartHourExplanation({ workerName: worker.name })}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setError(forcePrompt.rejectionMessage || null);
                    setForcePrompt(null);
                  }}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm font-medium text-ink-muted hover:text-ink rounded-lg transition disabled:opacity-50"
                >
                  No forzar
                </button>
                <button
                  type="button"
                  onClick={handleForce}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm font-semibold text-white bg-warning hover:opacity-90 rounded-lg transition disabled:opacity-60"
                >
                  Crear igualmente
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink rounded-lg transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || (isRangeMode && !validRange) || !!compRangeError}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition flex items-center gap-2 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Asignar
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignEventModal;
